import { FC } from "react";
import { useParams } from "react-router";
import { faLock } from "@fortawesome/pro-solid-svg-icons/faLock";
import { Badge } from "@postenbring/hedwig-react";
import Translated from "../infrastructure/translation/Translated";
import { useRootPath } from "../infrastructure/translation/useRootPath";
import { useTranslate } from "../infrastructure/translation/useTranslate";
import { ListRow, ListRows } from "../ListRow/ListRow";
import { SectionHeader } from "../Shared/SectionHeader";
import styles from "../Shared/SectionHeader.module.css";
import { useCertificatesList } from "./certificatesRouteContext";
import { statusOf } from "./certificateStatus";

export const CertificatesPage: FC = () => {
  const { orgnr } = useParams<{ orgnr: string }>();
  const { certificates, clients } = useCertificatesList();
  const rootPath = useRootPath();
  const translate = useTranslate();
  const organizationPath = `${rootPath}/organization/${orgnr}`;

  const clientsUsing = (certificateId: string) =>
    clients.filter((c) => c.client.certificateIds.includes(certificateId))
      .length;

  return (
    <section className={styles.section}>
      <SectionHeader
        titleKey="dashboard.certificates"
        descriptionKey="dashboard.certificates_description"
        actionKey="dashboard.new_certificate"
        actionTo={`${organizationPath}/certificates/new`}
      />

      {certificates.length === 0 ? (
        <p className={styles.emptyState}>
          <Translated textKey={"dashboard.no_certificates"} />
        </p>
      ) : (
        <ListRows>
          {certificates.map((cert) => {
            const count = clientsUsing(cert.certificate.id);
            const { isActive, certStatus } = statusOf(cert);

            return (
              <ListRow
                key={cert.certificate.id}
                to={`${organizationPath}/certificates/${cert.certificate.id}`}
                icon={faLock}
                title={cert.certificate.displayName}
                badges={
                  <Badge
                    data-color={isActive ? "success" : "error"}
                    size="smaller"
                  >
                    <Translated textKey={`certificate.status_${certStatus}`} />
                  </Badge>
                }
                meta={
                  count === 0
                    ? translate("dashboard.certificate_unused")
                    : translate("dashboard.certificate_used_by", { count })
                }
              />
            );
          })}
        </ListRows>
      )}
    </section>
  );
};
